/**
 * Host / subdomínio → app da SPA + geração centralizada de URLs públicas.
 *
 * Ambientes:
 * - localhost / *.vercel.app / development → modo LEGADO: todas as rotas no host atual
 *   e links públicos montados com o origin atual + rota interna.
 * - Produção (marimosca.com.br) → cada funcionalidade tem seu subdomínio oficial e os
 *   links públicos SEMPRE usam o subdomínio da funcionalidade, nunca o host atual.
 *   Anamnese / PAR-Q / fotos: onboarding.marimosca.com.br/anamnese|parq|enviar-fotos|equipamentos.
 *
 * Toda geração de URL pública deve passar por `buildPublicAppUrl` (ou pelos builders
 * de `src/utils/studentPublicLinks.ts` / `checkoutPublicLinks.ts`, que a utilizam).
 */

export type HostApp =
  | 'legacy'
  | 'disabled'
  | 'portal'
  | 'onboarding'
  | 'anamnese'
  | 'parq'
  | 'enviar-fotos'
  | 'equipamentos'
  | 'checkout'
  | 'quero-comecar'
  | 'fup';

export type PublicLinkApp = Exclude<HostApp, 'legacy' | 'disabled'>;

export const DOMAIN_ROOT = (process.env.REACT_APP_DOMAIN_ROOT || 'marimosca.com.br')
  .trim()
  .toLowerCase()
  .replace(/\/$/, '');

/**
 * Configuração canônica por app público:
 * - subdomain: subdomínio oficial em produção
 * - hostPath: path base quando servido no próprio subdomínio
 * - legacyPath: rota interna quando tudo roda no mesmo host (dev / vercel.app)
 */
const PUBLIC_APP_CONFIG: Record<PublicLinkApp, {
  subdomain: string;
  hostPath: string;
  legacyPath: string;
}> = {
  portal: { subdomain: 'portal', hostPath: '/', legacyPath: '/' },
  onboarding: { subdomain: 'onboarding', hostPath: '/', legacyPath: '/onboarding' },
  // Anamnese / PAR-Q / fotos usam o host onboarding com path dedicado.
  anamnese: { subdomain: 'onboarding', hostPath: '/anamnese', legacyPath: '/onboarding/anamnese' },
  parq: { subdomain: 'onboarding', hostPath: '/parq', legacyPath: '/onboarding/parq' },
  'enviar-fotos': { subdomain: 'onboarding', hostPath: '/enviar-fotos', legacyPath: '/onboarding/enviar-fotos' },
  equipamentos: {
    subdomain: 'onboarding',
    hostPath: '/equipamentos',
    legacyPath: '/onboarding/equipamentos',
  },
  checkout: { subdomain: 'checkout', hostPath: '/', legacyPath: '/checkout' },
  'quero-comecar': { subdomain: 'quero-comecar', hostPath: '/', legacyPath: '/quero-comecar' },
  fup: { subdomain: 'fup', hostPath: '', legacyPath: '/fup' },
};

const DEFAULT_HOST_APP_MAP: Record<string, HostApp> = {
  // Apex/www: redirect na Vercel → quero-comecar (defense: disabled se o SPA carregar).
  [DOMAIN_ROOT]: 'disabled',
  [`www.${DOMAIN_ROOT}`]: 'disabled',
  [`portal.${DOMAIN_ROOT}`]: 'portal',
  [`onboarding.${DOMAIN_ROOT}`]: 'onboarding',
  [`anamnese.${DOMAIN_ROOT}`]: 'anamnese',
  [`parq.${DOMAIN_ROOT}`]: 'parq',
  [`enviar-fotos.${DOMAIN_ROOT}`]: 'enviar-fotos',
  [`equipamentos.${DOMAIN_ROOT}`]: 'equipamentos',
  [`enviar-equipamentos.${DOMAIN_ROOT}`]: 'equipamentos',
  [`checkout.${DOMAIN_ROOT}`]: 'checkout',
  [`quero-comecar.${DOMAIN_ROOT}`]: 'quero-comecar',
  [`fup.${DOMAIN_ROOT}`]: 'fup',
};

const parseHostAppMap = (): Record<string, HostApp> => {
  const raw = process.env.REACT_APP_HOST_APP_MAP?.trim();
  if (!raw) {
    return { ...DEFAULT_HOST_APP_MAP };
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, string>;
    const next: Record<string, HostApp> = { ...DEFAULT_HOST_APP_MAP };

    Object.entries(parsed).forEach(([host, app]) => {
      const normalizedHost = host.trim().toLowerCase();
      const normalizedApp = app.trim() as HostApp;
      if (normalizedHost && normalizedApp) {
        next[normalizedHost] = normalizedApp;
      }
    });

    return next;
  } catch {
    console.error('[hostApps] REACT_APP_HOST_APP_MAP inválido; usando mapa padrão.');
    return { ...DEFAULT_HOST_APP_MAP };
  }
};

const HOST_APP_MAP = parseHostAppMap();

/** Overrides opcionais por env; sem eles o origin é derivado de DOMAIN_ROOT. */
const ORIGIN_ENV_BY_APP: Partial<Record<PublicLinkApp, string | undefined>> = {
  portal: process.env.REACT_APP_ORIGIN_PORTAL,
  onboarding: process.env.REACT_APP_ORIGIN_ONBOARDING,
  // Sempre o origin do onboarding (paths /anamnese, /parq, /enviar-fotos, /equipamentos).
  anamnese: process.env.REACT_APP_ORIGIN_ONBOARDING,
  parq: process.env.REACT_APP_ORIGIN_ONBOARDING,
  'enviar-fotos': process.env.REACT_APP_ORIGIN_ONBOARDING,
  equipamentos: process.env.REACT_APP_ORIGIN_ONBOARDING,
  checkout: process.env.REACT_APP_ORIGIN_CHECKOUT,
  'quero-comecar': process.env.REACT_APP_ORIGIN_QUERO_COMECAR,
  fup: process.env.REACT_APP_ORIGIN_FUP,
};

const normalizeOrigin = (value?: string | null): string => {
  const trimmed = value?.trim() ?? '';
  return trimmed.replace(/\/$/, '');
};

const isLocalHostname = (hostname: string): boolean => {
  const host = hostname.toLowerCase();
  return (
    host === 'localhost'
    || host === '127.0.0.1'
    || host.endsWith('.localhost')
    || host === '0.0.0.0'
  );
};

/** Preview / deploy default da Vercel (ex.: portal-alpha-liart.vercel.app). */
const isVercelAppHostname = (hostname: string): boolean => (
  hostname.toLowerCase().endsWith('.vercel.app')
);

/**
 * Hosts antigos do portal admin que devem ir para portal.marimosca.com.br.
 * Redirects do vercel.json só disparam em reload completo — a SPA também precisa
 * redirecionar nos cliques de navegação.
 */
const DEPRECATED_PORTAL_HOSTS = new Set([
  'portal-alpha-liart.vercel.app',
]);

export const isDeprecatedPortalHostname = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
): boolean => DEPRECATED_PORTAL_HOSTS.has(hostname.toLowerCase());

/** URL canônica no subdomínio oficial do portal (ignora host legado atual). */
export const buildCanonicalPortalUrl = (
  pathname: string,
  search = '',
  hash = '',
): string => {
  const origin = getCanonicalOriginForApp('portal');
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return `${origin}${path}${search}${hash}`;
};

/**
 * Legado = tudo no mesmo host (rotas internas):
 * localhost, *.vercel.app e development sem REACT_APP_FORCE_HOST_APPS.
 */
export const shouldUseLegacyHostRouting = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost',
  env = process.env.REACT_APP_ENV || process.env.NODE_ENV,
): boolean => {
  if (isLocalHostname(hostname) || isVercelAppHostname(hostname)) {
    return true;
  }

  if (env === 'development' && process.env.REACT_APP_FORCE_HOST_APPS !== 'true') {
    return true;
  }

  return false;
};

export const resolveHostApp = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost',
): HostApp => {
  if (shouldUseLegacyHostRouting(hostname)) {
    return 'legacy';
  }

  const mapped = HOST_APP_MAP[hostname.toLowerCase()];
  // Host desconhecido nunca cai no admin — fail closed.
  return mapped ?? 'disabled';
};

export const getCurrentHostApp = (): HostApp => resolveHostApp();

export const isFeatureHostApp = (app: HostApp): boolean => (
  app !== 'legacy' && app !== 'disabled' && app !== 'portal'
);

/** Apex e www devem sempre ir para Quero Começar. */
export const isApexOrWwwHostname = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
): boolean => {
  const host = hostname.toLowerCase();
  return host === DOMAIN_ROOT || host === `www.${DOMAIN_ROOT}`;
};

const getCurrentOrigin = (): string => (
  typeof window !== 'undefined' ? normalizeOrigin(window.location.origin) : ''
);

/**
 * Origin canônico de um app público em PRODUÇÃO.
 * Env override → senão derivado do domínio raiz (`https://<sub>.<DOMAIN_ROOT>`).
 * NUNCA usa o host atual da requisição.
 */
export const getCanonicalOriginForApp = (app: PublicLinkApp): string => {
  const fromEnv = normalizeOrigin(ORIGIN_ENV_BY_APP[app]);
  if (fromEnv) {
    return fromEnv;
  }

  return `https://${PUBLIC_APP_CONFIG[app].subdomain}.${DOMAIN_ROOT}`;
};

export const getQueroComecarCanonicalUrl = (): string => (
  `${getCanonicalOriginForApp('quero-comecar')}/`
);

export interface PublicAppUrlOptions {
  /** Sufixo de path (ex.: `/token` no FUP). */
  pathSuffix?: string;
  /** Query string SEM `?` (ex.: `s=abc` ou `p=hash&a=tok`). */
  search?: string;
}

/**
 * ÚNICO ponto de montagem de URL pública da aplicação.
 * - Legado (localhost / *.vercel.app / dev): origin atual + rota interna.
 * - Produção: subdomínio oficial da funcionalidade + path do subdomínio.
 */
export const buildPublicAppUrl = (
  app: PublicLinkApp,
  options: PublicAppUrlOptions = {},
): string => {
  const { pathSuffix = '', search = '' } = options;
  const config = PUBLIC_APP_CONFIG[app];
  const legacy = shouldUseLegacyHostRouting();

  const origin = legacy ? getCurrentOrigin() : getCanonicalOriginForApp(app);
  const basePath = legacy ? config.legacyPath : config.hostPath;

  let path = `${basePath}${pathSuffix}`;
  if (!path) {
    path = '/';
  }

  const query = search ? `${path.includes('?') ? '&' : '?'}${search}` : '';
  return `${origin}${path}${query}`;
};

/** Paths do fluxo de onboarding nas rotas internas (legado e host onboarding). */
export const getOnboardingRoutePaths = () => ({
  root: '/onboarding',
  anamnese: '/onboarding/anamnese',
  parq: '/onboarding/parq',
  parqAlias: '/onboarding/par-q',
  enviarFotos: '/onboarding/enviar-fotos',
  equipamentos: '/onboarding/equipamentos',
});

/** Paths de checkout conforme o host (achatados no subdomínio checkout). */
export const getCheckoutRoutePaths = (app: HostApp = getCurrentHostApp()) => {
  if (app === 'checkout') {
    return {
      root: '/',
      success: '/success',
      awaitingPayment: '/awaiting-payment',
    };
  }

  return {
    root: '/checkout',
    success: '/checkout/success',
    awaitingPayment: '/checkout/awaiting-payment',
  };
};

/** Paths de FUP público conforme o host. */
export const getFupRoutePaths = (app: HostApp = getCurrentHostApp()) => {
  const withStudentSlug = (token: string, studentSlug?: string): string => (
    `${token}${studentSlug?.trim() ? `/${studentSlug.trim()}` : ''}`
  );

  if (app === 'fup') {
    return {
      form: (token: string, studentSlug?: string) => `/${withStudentSlug(token, studentSlug)}`,
      thankYou: (token: string, studentSlug?: string) => `/${withStudentSlug(token, studentSlug)}/obrigado`,
    };
  }

  return {
    form: (token: string, studentSlug?: string) => `/fup/${withStudentSlug(token, studentSlug)}`,
    thankYou: (token: string, studentSlug?: string) => `/fup/${withStudentSlug(token, studentSlug)}/obrigado`,
  };
};

export const HOST_APP_LABELS: Record<HostApp, string> = {
  legacy: 'Legado (dev)',
  disabled: 'Desabilitado',
  portal: 'Portal',
  onboarding: 'Onboarding',
  anamnese: 'Anamnese',
  parq: 'PAR-Q',
  'enviar-fotos': 'Enviar fotos',
  equipamentos: 'Equipamentos',
  checkout: 'Checkout',
  'quero-comecar': 'Quero Começar',
  fup: 'FUP',
};
